/**
 * Pieces shared by every page of cards (Home, card tabs, filtered tabs): the column grid, the
 * drop area for rearranging, the bar that shows where a card will land, and card colours.
 */
import { Fragment, useRef, type ReactNode } from 'react'
import { useDroppable } from '@dnd-kit/core'
import { useTreeDnd } from '../dnd/TreeDnd'
import { distribute, useColumns } from '../lib/useColumns'

/** Cards in columns (1 on phones, 2–4 wider), each into the shortest column so far (SPEC §7.1). */
export function CardGrid<T>({ items, keyOf, height, render }: { items: T[]; keyOf: (t: T) => string; height: (t: T) => number; render: (t: T) => ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)
  const cols = useColumns(ref)
  const columns = distribute(items, cols, height)
  return (
    <div ref={ref} className="masonry" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
      {columns.map((col, i) => (
        <div key={i} className="masonry-col flex min-w-0 flex-col">
          {col.map((t) => (
            <Fragment key={keyOf(t)}>{render(t)}</Fragment>
          ))}
        </div>
      ))}
    </div>
  )
}

/**
 * Around a page's cards (one per page): while a card is dragged to rearrange, dropping in a
 * gap or below a column lands it by the nearest card.
 */
export function CardsArea({ enabled = true, rows = false, children }: { enabled?: boolean; rows?: boolean; children: ReactNode }) {
  const st = useTreeDnd()?.state
  // `rows`: a board, where a row dropped in the open (a gap, beside or below the cards) becomes a card there.
  const { setNodeRef } = useDroppable({ id: 'cards:area', disabled: !enabled || (st?.kind !== 'card' && !(rows && st?.kind === 'row')) })
  // What zooms out while a card is moved (lib/zoom).
  return (
    <div ref={setNodeRef} data-cards-area>
      {children}
    </div>
  )
}

/** Where a dragged card will land: a bar in the gap above or below this one (`label`: what it'll be, for a row becoming a card). */
export function DropBar({ side, label }: { side: 'before' | 'after' | 'inside' | null | undefined; label?: string }) {
  if (side !== 'before' && side !== 'after') return null
  return (
    // The label at the bar's right end: what's dragged hangs under the finger, mostly to its left.
    <div
      className={`pointer-events-none absolute inset-x-3 z-10 flex h-[3px] items-center justify-end rounded-sm bg-fg ${side === 'before' ? '-top-[0.47rem]' : '-bottom-[0.47rem]'}`}
      aria-hidden="true"
      data-drop-bar
    >
      {label && <span className="lab rounded-sm bg-fg px-2 py-0.5 text-bg">{label}</span>}
    </div>
  )
}

/** Over a card long enough to go inside it: the card outlined, and a label saying so. */
export function DropInside() {
  return (
    <div className="pointer-events-none absolute inset-0 z-10 flex items-start justify-center bg-fg/5 pt-3 ring-2 ring-fg ring-inset" aria-hidden="true">
      <span className="rounded-sm bg-fg px-3 py-1 text-xs font-medium text-bg shadow">Drop to put it inside</span>
    </div>
  )
}

export { tintClass, tintVar } from '../lib/tint'
